/* eslint-disable no-restricted-imports, react/forbid-elements */
import React, { forwardRef } from "react";
import type { LinkProps } from "next/link";
import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";
import { useAppSelector } from "@litres-core/store";
import { isNextJSDestinationEnv } from "./utils/test-destination-env";

declare global {
  interface Window {
    /**
     * Кастомный document.referrer
     *
     * Данная возможность фиксит стандартный document.referrer,
     * который пустой при клиентском роутинге после перехода на целевую страницу
     **/
    customReferrer?: string;
  }
}

export type SmartLinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof LinkProps
> &
  Omit<LinkProps, "href"> & {
    children?: React.ReactNode;
    href?: string;
    e2eId?: string;
  } & React.RefAttributes<HTMLAnchorElement>;

/**
 * Данный маневр необходим что бы в билды старого стека не включался некст, так как там это бесполезно и работать не будет.
 * На уровне webpack.production.js добавляем next, next/router, next/link в исключения билда
 * Так как js в рантайме не найдет некст, компонент вернет обычную ссылку
 * В nextjs все будет работать как и ожидается
 * Снести после того как все переедет в некст
 */
// eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires
const nextLinkModule = require("next/link");

let NextLinkComponent: React.FunctionComponent<SmartLinkProps>;

if (!isEmpty(nextLinkModule)) {
  NextLinkComponent = nextLinkModule.default;
}

export const SmartLink = forwardRef<HTMLAnchorElement, SmartLinkProps>(
  (props, ref) => {
    const { href, children, e2eId, onClick = noop, ...otherProps } = props;

    const isRobot = useAppSelector(state => state.browser.isRobot);

    const isNextPageHref = isEmpty(nextLinkModule)
      ? false
      : isNextJSDestinationEnv(href);

    const shouldUseNextLinkComponent =
      !isRobot && isNextPageHref && props.target !== "_blank";

    const handleNextLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      /**
       * Кастомный document.referrer
       *
       * Данная возможность фиксит стандартный document.referrer,
       * который пустой при клиентском роутинге после перехода на целевую страницу
       **/
      window.customReferrer = window.location.href;

      onClick(e);
    };

    const handleLinkClick = (e: React.SyntheticEvent) => {
      onClick(e);
      if (props.href && !e.defaultPrevented && props.target !== "_blank") {
        e.preventDefault();
        if (typeof window.parent !== "undefined") {
          window.parent.location.href = props.href;
          return;
        }
        window.location.href = props.href;
      }
    };

    return shouldUseNextLinkComponent ? (
      <NextLinkComponent
        ref={ref}
        prefetch={false}
        data-testid={e2eId}
        href={href}
        tabIndex={0}
        {...otherProps}
        onClick={e => {
          handleNextLinkClick(e);
        }}
        onKeyDown={e => {
          if (
            e.key === "Enter" ||
            (otherProps.role === "button" && e.key === " ")
          ) {
            window.customReferrer = window.location.href;
            if (otherProps.role === "button") {
              e.stopPropagation();
            }
            onClick(e);
          }
        }}
      >
        {children}
      </NextLinkComponent>
    ) : (
      <a
        ref={ref}
        data-testid={e2eId}
        href={href}
        tabIndex={0}
        {...otherProps}
        onClick={handleLinkClick}
        onKeyDown={e => {
          if (
            e.key === "Enter" ||
            (otherProps.role === "button" && e.key === " ")
          ) {
            if (otherProps.role === "button") {
              e.stopPropagation();
            }
            handleLinkClick(e);
          }
        }}
      >
        {children}
      </a>
    );
  }
);

SmartLink.displayName = "SmartLink";

export default SmartLink;
